/** Scoped enhancements; Horizon remains responsible for variants, media and cart mutations. */
class ObPdpOptions extends HTMLElement {
  connectedCallback() {
    if (!this.closest('.ob-pdp')) return;
    this.controller?.abort();
    this.controller = new AbortController();
    const signal = this.controller.signal;
    this.unit = this.unit || 'in';
    this.config = JSON.parse(this.querySelector('[data-ob-size-config]').textContent);
    this.ranges = new Map((this.config.ranges || '').split('\n').filter(Boolean).map(line => {
      const [name, inches, cm] = line.split('|').map(value => value.trim());
      return [name.toLowerCase(), { in: inches, cm }];
    }));
    this.addEventListener('change', event => {
      this.selectionChanged = true;
      if (event.target.matches('input[type="radio"]') && event.target.closest('variant-picker')) {
        this.refresh();
        return;
      }
      if (!event.target.matches('[data-ob-size-select]')) return;
      // The proxy has no option-value IDs; only the original radio triggers Horizon.
      event.stopImmediatePropagation();
      this.radios?.find(input => input.value === event.target.value)?.click();
    }, { capture: true, signal });
    this.addEventListener('click', event => {
      const button = event.target.closest('[data-ob-unit]');
      if (!button) return;
      this.unit = button.dataset.obUnit;
      this.refresh();
    }, { signal });
    // Some variant updates change attributes only; child-list observation misses them.
    this.addEventListener('variant:update', () => this.enhance(), { signal });
    this.observer = new MutationObserver(() => this.enhance());
    if (!this.querySelector('variant-picker')) return;
    this.observer.observe(this.querySelector('variant-picker'), { childList: true, subtree: true });
    this.enhance();
    this.selectInitialSize(signal);
  }
  disconnectedCallback() { this.observer?.disconnect(); this.controller?.abort(); }
  async selectInitialSize(signal) {
    if (this.initialSizeHandled) return;
    this.initialSizeHandled = true;
    const params = new URL(window.location.href).searchParams;
    if (!this.config.defaultSize || params.has('variant') || params.has('option_values')) return;
    // Wait for Horizon's listeners, then use its normal variant/media/price/form update path.
    await Promise.all([
      customElements.whenDefined('variant-picker'),
      customElements.whenDefined('product-form-component'),
      document.readyState === 'loading'
        ? new Promise(resolve => document.addEventListener('DOMContentLoaded', resolve, { once: true }))
        : Promise.resolve(),
    ]);
    if (signal.aborted || !this.isConnected || this.selectionChanged) return;
    const input = this.radios?.find(radio => radio.value === this.config.defaultSize);
    if (input && !input.checked) input.click();
  }
  formatSize(value, unit = this.unit) {
    const name = value.replace(/\s*[-–—]\s*/g, '–');
    const range = this.ranges.get(value.toLowerCase())?.[unit];
    if (!range) return name;
    const measurement = unit === 'in' ? range.replace(/\s*(?:["″]\s*)?in\s*$/i, '" in') : range;
    return `${name} (${measurement})`;
  }
  enhance() {
    const picker = this.querySelector('variant-picker');
    const fieldset = [...picker.querySelectorAll('fieldset')].find(item =>
      (item.dataset.obOptionName || item.querySelector('legend')?.textContent.trim()).toLowerCase() === this.config.option.toLowerCase());
    if (!fieldset) return;
    this.radios = [...fieldset.querySelectorAll('input[type=radio]')];
    fieldset.classList.add('ob-pdp__size-native');
    // Preserve native option indexes; the size proxy and guide live after the colors
    // outside the picker so changing variants cannot clone or reorder the guide.
    this.querySelector('.ob-pdp__size-row').hidden = false;
    this.refresh();
  }
  refresh() {
    const select = this.querySelector('[data-ob-size-select]');
    if (!select || !this.radios) return;
    this.observer?.disconnect();
    const selected = this.radios.find(input => input.checked);
    select.replaceChildren(...this.radios.map(input => {
      const option = new Option();
      option.value = input.value;
      option.textContent = this.formatSize(input.value);
      return option;
    }));
    select.value = selected?.value || '';
    const legend = this.querySelector('.ob-pdp__size-native .ob-pdp__selected-option');
    if (legend && selected) legend.textContent = selected.value.replace(/\s*[-–—]\s*/g, '–');
    const range = this.ranges.get(selected?.value.toLowerCase());
    const output = this.querySelector('[data-ob-range]');
    output.textContent = range ? [range.in, range.cm].filter(Boolean).join(' · ') : '';
    output.closest('p').hidden = !range;
    this.querySelectorAll('[data-ob-unit]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.obUnit === this.unit)));
    this.observer?.observe(this.querySelector('variant-picker'), { childList: true, subtree: true });
  }
}

class ObPdpDeliveryEstimate extends HTMLElement {
  connectedCallback() {
    const output = this.querySelector('[data-ob-delivery-range]');
    const min = Number(this.dataset.minDays);
    const max = Number(this.dataset.maxDays);
    if (!output || !Number.isInteger(min) || !Number.isInteger(max) || min < 1 || max < min) return;

    // Calendar days are counted in the visitor's local timezone; weekends do not count.
    const dateAfterBusinessDays = days => {
      const date = new Date();
      date.setHours(12, 0, 0, 0);
      let remaining = days;
      while (remaining > 0) {
        date.setDate(date.getDate() + 1);
        if (date.getDay() !== 0 && date.getDay() !== 6) remaining--;
      }
      return date;
    };
    const first = dateAfterBusinessDays(min);
    const last = dateAfterBusinessDays(max);
    const locale = document.documentElement.lang || 'en';
    const dateOptions = { day: 'numeric', month: 'long' };
    if (first.getFullYear() !== new Date().getFullYear() || last.getFullYear() !== first.getFullYear()) {
      dateOptions.year = 'numeric';
    }
    const formatter = new Intl.DateTimeFormat(locale, dateOptions);
    output.textContent = `${this.dataset.betweenLabel} ${formatter.format(first)} ${this.dataset.andLabel} ${formatter.format(last)}`;
  }
}

/** Mobile-only proxy for the PDP's native variant picker and product form. */
class ObPdpMobileBuy extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.productSection = this.closest('.shopify-section');
    this.pdp = this.closest('.ob-pdp');
    this.optionsContainer = this.querySelector('[data-ob-sticky-options]');
    this.button = this.querySelector('[data-ob-sticky-buy-button]');
    this.buttonText = this.querySelector('[data-ob-sticky-buy-text]');
    if (!this.productSection || !this.pdp || !this.optionsContainer || !this.button) return;

    this.addEventListener('change', event => {
      const select = event.target.closest('select[data-ob-sticky-option-index]');
      if (!select) return;
      const fieldset = this.getFieldsets()[Number(select.dataset.obStickyOptionIndex)];
      const input = [...(fieldset?.querySelectorAll('input[type="radio"]') || [])]
        .find(radio => radio.value === select.value);
      if (input && !input.checked) input.click();
    }, { signal });
    this.addEventListener('click', event => {
      const unitButton = event.target.closest('[data-ob-sticky-unit]');
      if (!unitButton) return;
      this.pdp.querySelector(`ob-pdp-options [data-ob-unit="${unitButton.dataset.obStickyUnit}"]`)?.click();
      this.scheduleSync();
    }, { signal });
    this.button.addEventListener('click', () => {
      const original = this.getOriginalButton();
      if (original && !original.disabled) original.click();
    }, { signal });
    this.productSection.addEventListener('change', event => {
      if (event.target.closest('variant-picker')) this.scheduleSync();
    }, { signal });
    this.productSection.addEventListener('click', event => {
      if (event.target.closest('ob-pdp-options [data-ob-unit]')) this.scheduleSync();
    }, { signal });
    this.productSection.addEventListener('variant:update', () => this.scheduleSync(), { signal });
    window.addEventListener('scroll', this.updateVisibility, { passive: true, signal });
    window.addEventListener('resize', this.updateVisibility, { passive: true, signal });

    const options = this.pdp.querySelector('ob-pdp-options');
    if (options) {
      this.optionsObserver = new MutationObserver(() => this.scheduleSync());
      this.optionsObserver.observe(options, { childList: true, subtree: true });
    }
    const form = this.pdp.querySelector('.buy-buttons-block product-form-component');
    if (form) {
      this.formObserver = new MutationObserver(() => this.scheduleSync());
      this.formObserver.observe(form, { attributes: true, attributeFilter: ['disabled'], childList: true, subtree: true });
    }
    this.sync();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.optionsObserver?.disconnect();
    this.formObserver?.disconnect();
  }

  getFieldsets() {
    return [...(this.pdp?.querySelectorAll('ob-pdp-options variant-picker fieldset[data-ob-option-name]') || [])]
      .filter(fieldset => fieldset.querySelectorAll('input[type="radio"]').length > 1);
  }

  getOriginalButton() {
    return this.pdp?.querySelector('.buy-buttons-block product-form-component [ref="addToCartButton"]');
  }

  scheduleSync() {
    if (this.syncQueued) return;
    this.syncQueued = true;
    queueMicrotask(() => {
      this.syncQueued = false;
      if (this.isConnected) this.sync();
    });
  }

  sync() {
    const fieldsets = this.getFieldsets();
    const sizeOptions = this.pdp.querySelector('ob-pdp-options');
    const sizeName = sizeOptions?.config?.option;
    const unit = sizeOptions?.unit || 'in';
    const signature = JSON.stringify(fieldsets.map(fieldset => [
      fieldset.dataset.obOptionName,
      [...fieldset.querySelectorAll('input[type="radio"]')].map(input => input.value),
      fieldset.classList.contains('variant-option--swatches'),
    ]));
    if (signature !== this.optionsSignature) {
      this.optionsSignature = signature;
      const controls = [];
      const colorControls = [];
      fieldsets.forEach((fieldset, index) => {
        const isSize = fieldset.dataset.obOptionName?.toLowerCase() === sizeName?.toLowerCase();
        const isSwatch = fieldset.classList.contains('variant-option--swatches');
        const label = document.createElement('label');
        label.className = isSwatch ? 'ob-pdp__mobile-buy-color' : 'ob-pdp__mobile-buy-option';
        if (isSize) label.classList.add('ob-pdp__mobile-buy-option--size');
        if (isSwatch) {
          const swatch = document.createElement('span');
          swatch.className = 'ob-pdp__mobile-buy-swatch';
          swatch.setAttribute('aria-hidden', 'true');
          label.append(swatch);
          const chevron = document.createElement('span');
          chevron.className = 'ob-pdp__mobile-buy-color-chevron';
          chevron.setAttribute('aria-hidden', 'true');
          label.append(chevron);
        } else {
          const heading = document.createElement('span');
          heading.textContent = fieldset.dataset.obOptionName;
          label.append(heading);
        }
        const select = document.createElement('select');
        select.dataset.obStickyOptionIndex = String(index);
        select.setAttribute('aria-label', fieldset.dataset.obOptionName);
        select.replaceChildren(...[...fieldset.querySelectorAll('input[type="radio"]')].map(input =>
          new Option(input.value, input.value)));
        label.append(select);
        if (isSwatch) colorControls.push(label);
        else controls.push(label);

        if (isSize && sizeOptions?.ranges?.size) {
          const units = document.createElement('div');
          units.className = 'ob-pdp__mobile-buy-units';
          units.setAttribute('role', 'group');
          units.setAttribute('aria-label', sizeOptions.querySelector('.ob-pdp__units')?.getAttribute('aria-label') || 'Units');
          for (const value of ['in', 'cm']) {
            const button = document.createElement('button');
            button.type = 'button';
            button.dataset.obStickyUnit = value;
            button.textContent = value.toUpperCase();
            units.append(button);
          }
          controls.push(units);
        }
      });
      this.optionsContainer.replaceChildren(...colorControls, ...controls);
    }
    this.optionsContainer.hidden = fieldsets.length === 0;
    fieldsets.forEach((fieldset, index) => {
      const selected = fieldset.querySelector('input[type="radio"]:checked');
      const select = this.optionsContainer.querySelector(`select[data-ob-sticky-option-index="${index}"]`);
      if (!select) return;
      if (selected) select.value = selected.value;
      if (fieldset.dataset.obOptionName?.toLowerCase() === sizeName?.toLowerCase()) {
        for (const option of select.options) {
          option.textContent = sizeOptions.formatSize(option.value, unit);
        }
      }
      const swatchContainer = select.parentElement.querySelector('.ob-pdp__mobile-buy-swatch');
      if (swatchContainer && selected) {
        const nativeSwatch = selected.closest('label')?.querySelector('.swatch');
        swatchContainer.replaceChildren(...(nativeSwatch ? [nativeSwatch.cloneNode(true)] : []));
        select.parentElement.title = `${fieldset.dataset.obOptionName}: ${selected.value}`;
      }
    });
    this.optionsContainer.querySelectorAll('[data-ob-sticky-unit]').forEach(button =>
      button.setAttribute('aria-pressed', String(button.dataset.obStickyUnit === unit)));

    const original = this.getOriginalButton();
    this.button.disabled = !original || original.disabled;
    const originalText = original?.querySelector('.add-to-cart-text__content')?.textContent.trim();
    if (originalText) this.buttonText.textContent = originalText;
    this.updateVisibility();
  }

  updateVisibility = () => {
    const mainBuy = this.pdp?.querySelector('.buy-buttons-block');
    const footer = document.querySelector('footer');
    const mobile = window.matchMedia('(max-width: 749px)').matches;
    this.hidden = !mobile || !mainBuy || mainBuy.getBoundingClientRect().bottom >= 0 ||
      (footer && footer.getBoundingClientRect().top <= window.innerHeight);
  };
}

if (!customElements.get('ob-pdp-options')) customElements.define('ob-pdp-options', ObPdpOptions);
if (!customElements.get('ob-pdp-delivery-estimate')) customElements.define('ob-pdp-delivery-estimate', ObPdpDeliveryEstimate);
if (!customElements.get('ob-pdp-mobile-buy')) customElements.define('ob-pdp-mobile-buy', ObPdpMobileBuy);
